import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AlertOctagon, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * ExpiredTenantGate — wraps the admin dashboard children and, when the
 * tenant's status is trial_expired or subscription_expired, forces the
 * admin onto /admin/upgrade no matter which inner route they navigate to.
 *
 * Allowed paths during an expired state:
 *   - /admin/upgrade                  (the renewal flow itself)
 *   - /admin/billing                  (manage subscription + plans)
 *   - /admin/wallet                   (top up wallet so renewal can charge)
 *
 * Everything else 302-replaces to /admin/upgrade so the admin can't get
 * stuck on a page that won't function (the backend already 402s most
 * mutating endpoints, but the redirect gives a much cleaner UX than
 * letting them click around to dead screens).
 */
const ALLOWED_WHILE_EXPIRED = [
  "/admin/upgrade",
  "/admin/billing",
  "/admin/wallet",
];

function isAllowedDuringExpiry(pathname) {
  return ALLOWED_WHILE_EXPIRED.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

export default function ExpiredTenantGate({ children }) {
  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/trial-status");
        if (alive) setTrial(data);
      } catch {
        // 403 (non-admin) or other — fall through; gate stays inert
        if (alive) setTrial(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    // Re-check after a successful renewal — AdminUpgrade dispatches this
    const onInvalidate = () => load();
    window.addEventListener("trial-status-invalidate", onInvalidate);
    return () => {
      alive = false;
      window.removeEventListener("trial-status-invalidate", onInvalidate);
    };
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[hsl(var(--kb-muted))]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const expired = trial?.is_expired === true;

  if (expired && !isAllowedDuringExpiry(location.pathname)) {
    return <Navigate to="/admin/upgrade" replace />;
  }

  // Inline reminder pinned above the allowed pages so the admin always
  // knows why their nav is locked. (TrialBanner already shows this on
  // /admin, but the other allowed pages don't render TrialBanner.)
  if (expired) {
    return (
      <>
        <div
          className="border border-red-300 bg-red-50 text-red-900 px-4 py-2.5 flex items-center gap-3 text-sm mb-4"
          data-testid="expired-tenant-gate-banner"
        >
          <AlertOctagon size={16} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-medium">
              {trial?.expiry_reason === "subscription"
                ? "Subscription expired."
                : "Trial ended."}
            </span>{" "}
            Your storefront is paused. The only sections available are billing,
            wallet top-up, and renewal — everything else will unlock once you
            pay.
          </div>
        </div>
        {children}
      </>
    );
  }

  return children;
}
